import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, HStack, IconButton, Text, VStack } from "@chakra-ui/react";
import { Plus, X } from "lucide-react";
import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog";
import { Field, FieldContent, FieldDescription, FieldLabel, FieldTitle } from "@/components/ui/field";
import { Button } from "@/components/ui/button";
import type { Backtest } from "../../../../types/backtests";
import type { TradePolicySimulateResponse } from "../../../../types/tradePolicy";
import { ReportTradeReviewPanel, type ReportTradeReviewPanelProps } from "../ReportTradeReviewPanel";
import { useReportsDetailColors } from "../reportsDetailTheme";
import {
  REPORT_DETAIL_CARD_BORDER,
  reportDetailSectionCardProps,
} from "./ReportsCardChrome";
import {
  readReportDetailUiState,
  writeReportDetailUiState,
} from "../../../../utils/reportDetailUiPersistence";
import {
  DASHBOARD_LAYOUT_FIELD_DESC_CLASS,
  DASHBOARD_LAYOUT_FIELD_LABEL_CLASS,
} from "../../Dashboard/components/dashboardLayoutUi";

type ComparisonPanel = {
  key: string;
  overlayBacktestId: string;
  overlayEnabled: boolean;
};

export type ReportComparisonTradeReviewStackProps = Omit<
  ReportTradeReviewPanelProps,
  | "overlayBacktestId"
  | "onOverlayBacktestIdChange"
  | "overlayEnabled"
  | "onOverlayEnabledChange"
  | "policyOverlay"
  | "backtestId"
  | "backtestSimulationRows"
  | "simulatedLegs"
  | "simulationOverlayMode"
  | "showSectionHeader"
  | "sectionDescription"
> & {
  isTmSimulationOverlay: (backtest: Backtest | undefined) => boolean;
  simulationOverlayModeForReview?: string;
  getBacktestSimulationRows: (backtestId: string) => Array<Record<string, unknown>> | undefined;
  getSimulatedLegs: (backtestId: string) => Array<"sl" | "tp"> | undefined;
};

function policyOverlayFromBacktest(bt: Backtest | undefined) {
  const result = bt?.result as TradePolicySimulateResponse | undefined;
  if (!result) return null;
  const kept = Array.isArray(result.kept_trade_ids) ? result.kept_trade_ids : [];
  const removed = Array.isArray(result.removed_trade_ids) ? result.removed_trade_ids : [];
  if (kept.length === 0 && removed.length === 0) return null;
  return { kept_trade_ids: kept, removed_trade_ids: removed };
}

function backtestKindLabel(bt: Backtest, isTm: boolean): string {
  return isTm ? "Exit placement overlay" : "Regime kept/removed overlay";
}

function panelTitle(backtest: Backtest | undefined, index: number): string {
  if (backtest?.title?.trim()) return backtest.title.trim();
  if (backtest) return `Comparison ${index + 1}`;
  return `Chart ${index + 1}`;
}

export function ReportComparisonTradeReviewStack(
  props: ReportComparisonTradeReviewStackProps
): React.ReactElement {
  const {
    linkedBacktests,
    isTmSimulationOverlay,
    simulationOverlayModeForReview,
    getBacktestSimulationRows,
    getSimulatedLegs,
    ...sharedProps
  } = props;

  const { subtleText } = useReportsDetailColors();
  const reportId = sharedProps.reportId;
  const [panels, setPanels] = useState<ComparisonPanel[]>(() => {
    const saved = readReportDetailUiState(reportId).comparisonPanels;
    const validIds = new Set(linkedBacktests.map((b) => b.id));
    return saved
      .filter((panel) => validIds.has(panel.overlayBacktestId))
      .map((panel) => ({
        key: `cmp-${panel.overlayBacktestId}-${Date.now()}`,
        overlayBacktestId: panel.overlayBacktestId,
        overlayEnabled: panel.overlayEnabled,
      }));
  });
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    const id = String(reportId ?? "").trim();
    if (!id) return;
    writeReportDetailUiState(id, {
      comparisonPanels: panels.map((panel) => ({
        overlayBacktestId: panel.overlayBacktestId,
        overlayEnabled: panel.overlayEnabled,
      })),
    });
  }, [reportId, panels]);

  useEffect(() => {
    const validIds = new Set(linkedBacktests.map((b) => b.id));
    setPanels((prev) => {
      const next = prev.filter((panel) => validIds.has(panel.overlayBacktestId));
      return next.length === prev.length ? prev : next;
    });
  }, [linkedBacktests]);

  const availableToAdd = useMemo(
    () => linkedBacktests.filter((b) => !panels.some((p) => p.overlayBacktestId === b.id)),
    [linkedBacktests, panels]
  );

  const addPanel = useCallback((backtestId: string) => {
    setPanels((prev) => [
      ...prev,
      {
        key: `cmp-${backtestId}-${Date.now()}`,
        overlayBacktestId: backtestId,
        overlayEnabled: true,
      },
    ]);
    setPickerOpen(false);
  }, []);

  const removePanel = useCallback((key: string) => {
    setPanels((prev) => prev.filter((p) => p.key !== key));
  }, []);

  const updatePanel = useCallback((key: string, patch: Partial<ComparisonPanel>) => {
    setPanels((prev) => prev.map((p) => (p.key === key ? { ...p, ...patch } : p)));
  }, []);

  return (
    <Box mt={6} data-report-comparison-trade-review="">
      <HStack justify="space-between" align="flex-start" mb={4} flexWrap="wrap" gap={3}>
        <Box>
          <Text
            fontSize={{ base: "xl", md: "2xl" }}
            color="#38BBD1"
            fontWeight="normal"
            fontFamily="'Urbanist', sans-serif"
            lineHeight="1.15"
          >
            Comparison charts
          </Text>
          <Text fontSize="sm" color={subtleText} mt={1} fontFamily="'IBM Plex Sans', sans-serif" maxW="640px">
            Add extra trade-review charts to compare simulations side by side. Each chart has its own overlay
            selection.
          </Text>
        </Box>
        <Button
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          className="h-8 shrink-0 px-5 uppercase tracking-wide active:translate-y-0"
          onClick={() => setPickerOpen(true)}
          disabled={availableToAdd.length === 0}
        >
          <Plus size={16} className="shrink-0 text-white" aria-hidden />
          Add chart
        </Button>
      </HStack>

      {panels.length === 0 ? (
        <Box {...reportDetailSectionCardProps} py={8} px={4} textAlign="center">
          <Text fontSize="sm" color={subtleText} fontFamily="'IBM Plex Sans', sans-serif">
            {linkedBacktests.length === 0
              ? "Save a simulation to add comparison charts."
              : "Use Add chart to open another trade review with a different simulation overlay."}
          </Text>
        </Box>
      ) : (
        <VStack align="stretch" spacing={6}>
          {panels.map((panel, index) => {
            const bt = linkedBacktests.find((b) => b.id === panel.overlayBacktestId);
            const isTm = isTmSimulationOverlay(bt);
            const policyOverlay =
              !isTm && panel.overlayEnabled ? policyOverlayFromBacktest(bt) : null;

            return (
              <Box key={panel.key} {...reportDetailSectionCardProps} overflow="hidden">
                <HStack
                  px={4}
                  py={3}
                  borderBottomWidth="1px"
                  borderBottomStyle="solid"
                  borderBottomColor={REPORT_DETAIL_CARD_BORDER}
                  justify="space-between"
                >
                  <Text
                    fontSize="md"
                    fontWeight="semibold"
                    fontFamily="'Urbanist', sans-serif"
                    color={subtleText}
                  >
                    {panelTitle(bt, index)}
                  </Text>
                  <IconButton
                    aria-label="Remove comparison chart"
                    icon={<X size={16} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => removePanel(panel.key)}
                  />
                </HStack>
                <Box px={{ base: 3, md: 4 }} py={{ base: 3, md: 4 }}>
                  <ReportTradeReviewPanel
                    {...sharedProps}
                    linkedBacktests={linkedBacktests}
                    overlayBacktestId={panel.overlayBacktestId}
                    onOverlayBacktestIdChange={(id) =>
                      updatePanel(panel.key, { overlayBacktestId: id })
                    }
                    overlayEnabled={panel.overlayEnabled}
                    onOverlayEnabledChange={(enabled) =>
                      updatePanel(panel.key, { overlayEnabled: enabled })
                    }
                    overlayTradeIds={
                      policyOverlay
                        ? {
                            kept: policyOverlay.kept_trade_ids,
                            removed: policyOverlay.removed_trade_ids,
                          }
                        : null
                    }
                    overlayTradeIdsLoading={false}
                    overlayTradeIdsError={null}
                    policyOverlay={policyOverlay}
                    backtestId={panel.overlayEnabled ? panel.overlayBacktestId : undefined}
                    simulationOverlayMode={isTm ? simulationOverlayModeForReview : undefined}
                    backtestSimulationRows={getBacktestSimulationRows(panel.overlayBacktestId)}
                    simulatedLegs={getSimulatedLegs(panel.overlayBacktestId)}
                    sectionDescription={`Overlay: ${panelTitle(bt, index)}. Independent from the primary trade review above.`}
                    uiPersistenceKey={`comparison-${panel.overlayBacktestId}`}
                  />
                </Box>
              </Box>
            );
          })}
        </VStack>
      )}

      <StrategyPineDetailDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title="Add comparison chart"
        description="Pick a saved simulation to open in a new trade review chart."
        showHeader
        dismissLabel="Close"
        contentClassName="sm:max-w-2xl"
        bodyClassName="flex-none overflow-visible"
        contentProps={{ "data-report-add-comparison-chart-dialog": "" }}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {availableToAdd.map((bt) => (
            <FieldLabel
              key={bt.id}
              role="button"
              tabIndex={0}
              className="has-data-checked:!border-transparent has-data-checked:!bg-transparent dark:has-data-checked:!border-transparent dark:has-data-checked:!bg-transparent"
              onClick={() => addPanel(bt.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  addPanel(bt.id);
                }
              }}
            >
              <Field>
                <FieldContent>
                  <FieldTitle className={DASHBOARD_LAYOUT_FIELD_LABEL_CLASS}>
                    {bt.title?.trim() || `Simulator ${bt.id.slice(0, 8)}…`}
                  </FieldTitle>
                  <FieldDescription className={DASHBOARD_LAYOUT_FIELD_DESC_CLASS}>
                    {backtestKindLabel(bt, isTmSimulationOverlay(bt))}
                  </FieldDescription>
                </FieldContent>
              </Field>
            </FieldLabel>
          ))}
        </div>
      </StrategyPineDetailDialog>
    </Box>
  );
}
